import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import API_BASE_URL from "./config/api";
import styled, { keyframes } from "styled-components";
import Navbar from "./components/Navbar";
import OurOffer from "./components/OurOffer";
import Footer from "./components/Footer";
import FloatingAIButton from "./components/FloatingAIButton";
import destinationHero from "./assests/destination.png";
import Modal from "./Modal";

// --- Keyframes ---
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

// --- Styled Components ---

const PageContainer = styled.div`
  background-color: #f8f9fa;
  min-height: 100vh;
`;

const HeroSection = styled.header`
  width: 100%;
  height: 60vh;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    height: 40vh;
  }
`;

const HeroImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  transition: transform 0.5s ease;

  ${HeroSection}:hover & {
    transform: scale(1.05);
  }
`;

const HeroOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.6) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HeroTitle = styled.h1`
  font-family: "MonteCarlo", cursive;
  color: white;
  font-size: 5rem;
  text-shadow: 0 4px 6px rgba(0,0,0,0.3);
  animation: ${fadeIn} 1s ease-out;

  @media (max-width: 768px) {
    font-size: 3.5rem;
  }
`;

const ContentContainer = styled.div`
  max-width: 1400px;
  margin: -80px auto 40px;
  padding: 0 20px;
  position: relative;
  z-index: 2;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  height: 450px;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 78, 97, 0.2);
  }

  @media (max-width: 480px) {
    height: 400px;
  }
`;

const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.6s ease;
  filter: brightness(0.9);

  ${Card}:hover & {
    transform: scale(1.1);
    filter: brightness(0.7);
  }
`;

const CardContent = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 2rem;
  background: linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.5) 60%, transparent 100%);
  color: white;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  height: 100%;
  transition: all 0.3s ease;
  transform: translateY(0);
`;

const CardTitle = styled.h3`
  font-family: "Merriweather", serif;
  font-size: 1.8rem;
  margin-bottom: 0.5rem;
  text-shadow: 0 2px 4px rgba(0,0,0,0.5);

  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const Tag = styled.span`
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(5px);
  padding: 6px 12px;
  border-radius: 20px;
  font-family: "Poppins", sans-serif;
  font-size: 0.85rem;
  margin-bottom: 1rem;
  align-self: flex-start;
  border: 1px solid rgba(255,255,255,0.3);
`;

const CardDescription = styled.p`
  font-family: "Poppins", sans-serif;
  font-size: 0.95rem;
  line-height: 1.6;
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.4s ease;
  margin-bottom: 1.5rem;
  max-height: 0;
  overflow: hidden;
  color: #e0e0e0;

  ${Card}:hover & {
    opacity: 1;
    transform: translateY(0);
    max-height: 100px;
  }
`;

const ActionButton = styled.button`
  background-color: #1CA8CB;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 30px;
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  opacity: 0;
  transform: translateY(20px);
  align-self: flex-start;
  box-shadow: 0 4px 15px rgba(28, 168, 203, 0.4);

  ${Card}:hover & {
    opacity: 1;
    transform: translateY(0);
  }

  &:hover {
    background-color: white;
    color: #1CA8CB;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background: #f8f9fa;
`;

const Spinner = styled.div`
  width: 50px;
  height: 50px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #1CA8CB;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

const FloatingButton = styled.button`
  position: fixed;
  bottom: 20px;
  right: 115px;
  background: none;
  border: none;
  cursor: pointer;
  z-index: 1000;
  transition: transform 0.3s ease;
  filter: drop-shadow(0 4px 8px rgba(0,0,0,0.2));

  &:hover {
    transform: scale(1.1) rotate(5deg);
  }

  img {
    width: 65px;
    height: 65px;
    @media (max-width: 480px) {
      width: 55px;
      height: 55px;
    }
  }
`;

function Destination() {
  const navigate = useNavigate();
  const location = useLocation();
  const selectedState = location.state?.selectedState;
  const [destinations, setDestinations] = useState([]);
  const [packageCounts, setPackageCounts] = useState({});
  const [isModalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const handleViewTours = (destinationName) => {
    navigate(`/DestinationPackages?country=${destinationName}`);
  };

  const openModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);

  useEffect(() => {
    if (!selectedState) {
      console.error("No state selected");
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        const [destResponse, pkgResponse] = await Promise.all([
          fetch(`${API_BASE_URL}/destinations/state/${selectedState}`),
          fetch(`${API_BASE_URL}/packages`)
        ]);

        const destinationsData = await destResponse.json();
        const packagesData = await pkgResponse.json();

        // Filter to only show active destinations
        const activeDestinations = destinationsData.filter(dest => dest.isActive !== false);
        setDestinations(activeDestinations);

        // Calculate package counts for each destination
        const counts = {};

        // Initialize counts to 0
        activeDestinations.forEach(dest => {
          counts[dest._id] = 0;
        });

        // Loop through active packages and increment counts
        packagesData.forEach(pkg => {
          if (pkg.isActive) {
            const visitedDestinations = new Set();

            // Check itinerary for destinations
            if (pkg.itinerary && Array.isArray(pkg.itinerary)) {
              pkg.itinerary.forEach(day => {
                if (day.destinations && Array.isArray(day.destinations)) {
                  day.destinations.forEach(destObj => {
                    const destId = destObj.destinationId?._id || destObj.destinationId;
                    if (destId) {
                      visitedDestinations.add(String(destId));
                    }
                  });
                }
              });
            }

            // Increment count for each unique destination in this package
            visitedDestinations.forEach(destId => {
              if (counts[destId] !== undefined) {
                counts[destId]++;
              }
            });
          }
        });

        setPackageCounts(counts);
        setLoading(false);

      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchData();

    const timer = setTimeout(openModal, 12000);
    return () => clearTimeout(timer);
  }, [selectedState]);

  if (loading) {
    return (
      <LoadingContainer>
        <Spinner />
      </LoadingContainer>
    );
  }

  return (
    <PageContainer>
      <Navbar />

      <HeroSection>
        <HeroImage src={destinationHero} alt="Destination Hero" />
        <HeroOverlay>
          <HeroTitle>{selectedState || "Explore Destinations"}</HeroTitle>
        </HeroOverlay>
      </HeroSection>

      <ContentContainer>
        <Grid>
          {destinations.map((destination) => (
            <Card
              key={destination._id}
              onClick={() => handleViewTours(destination.name)}
            >
              <CardImage src={destination.images[0]} alt={destination.name} />
              <CardContent>
                <CardTitle>{destination.name}</CardTitle>
                <Tag>
                  {packageCounts[destination._id] || 0} Packages Available
                </Tag>
                {destination.description && (
                  <>
                    <CardDescription>
                      {destination.description.slice(0, 100)}...
                    </CardDescription>
                    <ActionButton>
                      View Packages
                    </ActionButton>
                  </>
                )}
              </CardContent>
            </Card>
          ))}
        </Grid>
      </ContentContainer>

      <OurOffer />
      <Footer />

      <FloatingAIButton onClick={openModal} />

      {isModalOpen && <Modal closeModal={closeModal} />}
    </PageContainer>
  );
}

export default Destination;